(() => {
  'use strict';

  const PRODUCT_SELECTOR = '[data-section-type="product"]';
  const OPTION_SELECTOR = '[name^="options["]';
  const POLL_MS = 200;

  function getVariants(container) {
    const jsonEl = container.querySelector('[data-variant-json]');
    if (!jsonEl) return [];

    try {
      const variants = JSON.parse(jsonEl.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.warn('[Trotie] Variant JSON inválido.', error);
      return [];
    }
  }

  function getOptionInputs(container) {
    return [...container.querySelectorAll(OPTION_SELECTOR)]
      .filter((el) => {
        if (el.disabled) return false;

        if (el.type === 'radio' || el.type === 'checkbox') {
          return el.checked;
        }

        return true;
      });
  }

  function getSelectedValues(container) {
    return getOptionInputs(container).map((el) => el.value);
  }

  function getSelectedVariant(container) {
    const selected = getSelectedValues(container);
    const variants = getVariants(container);

    return (
      variants.find((variant) => {
        return (
          Array.isArray(variant.options) &&
          variant.options.length === selected.length &&
          variant.options.every(
            (value, index) => String(value) === String(selected[index])
          )
        );
      }) || null
    );
  }

  function isColorInput(input) {
    const name = (input.getAttribute('name') || '').toLowerCase();
    const wrapperText = (
      input.closest('.product__option')?.textContent || ''
    ).toLowerCase();

    return (
      name.includes('[cor]') ||
      name.includes('[color]') ||
      name.includes('[colour]') ||
      wrapperText.includes('cor:') ||
      wrapperText.includes('color:') ||
      wrapperText.includes('colour:')
    );
  }

  function getColorInput(container) {
    return getOptionInputs(container).find(isColorInput) || null;
  }

  function updateLabel(input) {
    const wrapper = input.closest('.product__option');
    const label = wrapper?.querySelector(
      '[data-selected-value-for-option]'
    );

    if (label) {
      label.textContent = input.value;
    }
  }

  function syncVariantId(container, variant) {
    if (!variant) return;

    const variantId = String(variant.id);

    const variantSelect = container.querySelector(
      '[data-variant-select]'
    );

    if (variantSelect) {
      variantSelect.value = variantId;

      [...variantSelect.options].forEach((option) => {
        option.selected = String(option.value) === variantId;
      });
    }

    container
      .querySelectorAll(
        'form[action*="/cart/add"] [name="id"], [data-product-form] [name="id"]'
      )
      .forEach((input) => {
        input.value = variantId;
      });

    const productForm = container.querySelector('[data-product-form]');

    if (productForm) {
      productForm.dataset.currentProductId = variantId;
    }

    container.dataset.currentProductId = variantId;

    container
      .querySelectorAll('[data-current-variant-id]')
      .forEach((el) => {
        el.dataset.currentVariantId = variantId;
      });
  }

  function updateUrl(variant) {
    if (!variant) return;

    const url = new URL(window.location.href);

    if (url.searchParams.get('variant') === String(variant.id)) {
      return;
    }

    url.searchParams.set('variant', String(variant.id));

    window.history.replaceState(
      { path: url.toString() },
      '',
      url.toString()
    );
  }

  function updateBuyButtons(container, variant) {
    const buttons = container.querySelectorAll('[data-add-to-cart]');

    buttons.forEach((button) => {
      const text = button.querySelector('[data-add-to-cart-text]');

      const availableText =
        button.dataset.langAvailable || 'Adicionar ao carrinho';

      const unavailableText =
        button.dataset.langUnavailable || 'Indisponível';

      const soldOutText =
        button.dataset.langSoldOut || 'Esgotado';

      if (!variant) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');

        if (text) {
          text.textContent = unavailableText;
        }

        return;
      }

      if (variant.available) {
        button.disabled = false;
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');

        if (text) {
          text.textContent = availableText;
        }
      } else {
        button.disabled = true;
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');

        if (text) {
          text.textContent = soldOutText;
        }
      }
    });
  }

  function updateDynamicCheckout(container, variant) {
    const paymentButtons = container.querySelectorAll(
      '.shopify-payment-button button'
    );

    paymentButtons.forEach((button) => {
      button.disabled = !variant || !variant.available;
    });
  }

  function handleCurrentSelection(container) {
    const variant = getSelectedVariant(container);

    if (!variant) {
      updateBuyButtons(container, null);
      updateDynamicCheckout(container, null);
      return;
    }

    syncVariantId(container, variant);
    updateUrl(variant);
    updateBuyButtons(container, variant);
    updateDynamicCheckout(container, variant);

    const colorInput = getColorInput(container);

    if (colorInput) {
      updateLabel(colorInput);
    }
  }

  function initProduct(container) {
    if (container.dataset.trotieVariantWatcher === 'true') {
      return;
    }

    container.dataset.trotieVariantWatcher = 'true';

    let lastVariantId = null;

    setInterval(() => {
      const variant = getSelectedVariant(container);
      const currentVariantId = variant
        ? String(variant.id)
        : 'none';

      if (currentVariantId !== lastVariantId) {
        lastVariantId = currentVariantId;
        handleCurrentSelection(container);
      }
    }, POLL_MS);

    container
      .querySelectorAll(OPTION_SELECTOR)
      .forEach((input) => {
        input.addEventListener('change', () => {
          handleCurrentSelection(container);
        });
      });

    handleCurrentSelection(container);
  }

  function initAll() {
    document
      .querySelectorAll(PRODUCT_SELECTOR)
      .forEach(initProduct);
  }

  document.addEventListener(
    'DOMContentLoaded',
    initAll
  );

  window.addEventListener(
    'load',
    initAll
  );

  initAll();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (
        !form.matches(
          '[data-product-form], form[action*="/cart/add"]'
        )
      ) {
        return;
      }

      const container = form.closest(PRODUCT_SELECTOR);

      if (!container) {
        return;
      }

      const variant = getSelectedVariant(container);

      if (!variant || variant.available === false) {
        event.preventDefault();
        event.stopImmediatePropagation();

        updateBuyButtons(container, variant);

        return;
      }

      syncVariantId(container, variant);
    },
    true
  );
})();